import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  Clock,
  FileBarChart,
  Plus,
  RefreshCw,
  Sparkles,
  UserPlus,
} from "lucide-react";
import { useAppSelector } from "../../../store/hooks";
import { selectUser } from "../../../store/selectors";
import styles from "./CommandBar.module.css";

const greetingFor = (h) =>
  h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";

// The dashboard's masthead. Keeps the Sunsky blue banner, tightened up and
// carrying the live clock, data freshness and the three actions people
// actually reach for.
const CommandBar = ({ onRefresh, loading, updatedAt }) => {
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const firstName = useMemo(
    () => (user?.name || "Admin").trim().split(" ")[0],
    [user]
  );

  const dateLabel = now.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
  const timeLabel = now.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const seconds = now.toLocaleTimeString("en-GB", { second: "2-digit" });

  const freshness = useMemo(() => {
    if (!updatedAt) return "Syncing";
    const secs = Math.max(0, Math.round((now.getTime() - updatedAt) / 1000));
    if (secs < 60) return "Updated just now";
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `Updated ${mins}m ago`;
    return `Updated ${Math.floor(mins / 60)}h ago`;
  }, [updatedAt, now]);

  return (
    <header className={styles.hero}>
      <span className={styles.orbA} aria-hidden="true" />
      <span className={styles.orbB} aria-hidden="true" />
      <span className={styles.mesh} aria-hidden="true" />

      <div className={styles.left}>
        <span className={styles.eyebrow}>
          <Sparkles size={12} strokeWidth={2.4} />
          Sunsky Control Center
        </span>

        <h1 className={styles.greeting}>
          {greetingFor(now.getHours())},{" "}
          <span className={styles.name}>{firstName}</span>
        </h1>

        <p className={styles.subtitle}>
          Here&apos;s everything happening across your travel operations today.
        </p>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primary}
            onClick={() => navigate("/bookings/add")}
          >
            <Plus size={15} strokeWidth={2.6} />
            New Booking
          </button>
          <button
            type="button"
            className={styles.ghost}
            onClick={() => navigate("/people/customers/private")}
          >
            <UserPlus size={14} strokeWidth={2.2} />
            Add Customer
          </button>
          <button
            type="button"
            className={styles.ghost}
            onClick={() => navigate("/finance/transactions")}
          >
            <FileBarChart size={14} strokeWidth={2.2} />
            Reports
          </button>
          <button
            type="button"
            className={styles.iconGhost}
            onClick={onRefresh}
            disabled={loading}
            title="Refresh dashboard data"
            aria-label="Refresh dashboard data"
          >
            <RefreshCw
              size={14}
              strokeWidth={2.3}
              className={loading ? styles.spinning : undefined}
            />
          </button>
        </div>
      </div>

      <div className={styles.right}>
        <div className={styles.clockCard}>
          <div className={styles.time}>
            <Clock size={15} className={styles.clockIcon} />
            <span className={`dshNum ${styles.timeValue}`}>{timeLabel}</span>
            <span className={`dshNum ${styles.seconds}`}>:{seconds}</span>
          </div>

          <div className={styles.dateRow}>
            <CalendarDays size={12} />
            {dateLabel}
          </div>

          <div className={styles.live}>
            <span className={styles.liveDot} />
            Live
            <i className={styles.liveSep} />
            <span className={styles.fresh}>{freshness}</span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default CommandBar;
